import { createSelector } from '@ngrx/store';
import { uploaderFeature } from '@shared/components/uploader/store/uploader.feature';
import { formatTime } from '@shared/components/uploader/helpers/format-remaining-time';
import { Truthy } from '@3dsource/utils';

export const selectCurrentUploadingFileIndex = createSelector(
  uploaderFeature.selectFiles,
  uploaderFeature.selectCurrentUploadId,
  (files, uploadId) => {
    if (!files?.length) {
      return null;
    }

    const fileIndex = files.findIndex((file) => file.uploadId === uploadId);

    return fileIndex !== -1 ? fileIndex : 0;
  },
);

export const selectTransformRemainingTime = createSelector(
  uploaderFeature.selectRemainingTime,
  (time) =>
    Truthy(time) ? `Time Remaining: ${formatTime(time)}` : 'Calculating...',
);

export const selectIsUploadingFiles = createSelector(
  uploaderFeature.selectFiles,
  (files) =>
    files?.length
      ? files?.some(
          (file) =>
            file.status === 'in_queue' ||
            file.status === 'uploading' ||
            file.status === 'processing',
        )
      : false,
);

export const selectUploadingFiles = createSelector(
  uploaderFeature.selectFiles,
  (files) =>
    files?.length
      ? files?.filter(
          (file) => file.status === 'uploading' || file.status === 'in_queue',
        )
      : [],
);

export const selectProcessingFiles = createSelector(
  uploaderFeature.selectFiles,
  (files) =>
    files?.length ? files?.filter((file) => file.status === 'processing') : [],
);

export const selectCurrentUploadingFile = createSelector(
  uploaderFeature.selectFiles,
  selectCurrentUploadingFileIndex,
  (files, currentFileIndex) =>
    files?.length ? files[currentFileIndex || 0] : null,
);

export const selectIsHasProcessingFiles = createSelector(
  uploaderFeature.selectFiles,
  (files) =>
    files?.length ? files?.some((file) => file.status === 'processing') : null,
);

export const selectIsAllChunksUploading = createSelector(
  selectCurrentUploadingFile,
  (file) => file?.isCompleted ?? file,
);

export const selectPreparedReadyAssetIds = createSelector(
  uploaderFeature.selectCreatedAssets,
  (assets) => {
    const environments: string[] = [];
    const products: string[] = [];
    const materials: string[] = [];

    Object.values(assets).map((value) => {
      if (value.assetType === 'environment') {
        environments.push(value.assetId);
      }

      if (value.assetType === 'product') {
        products.push(value.assetId);
      }

      if (value.assetType === 'material') {
        materials.push(value.assetId);
      }
    });

    return {
      environments: environments.join(','),
      products: products.join(','),
      materials: materials.join(','),
    };
  },
);

export const selectIsExistsProductsOrEnvironmentsIds = createSelector(
  selectPreparedReadyAssetIds,
  (assetsIds) => !!assetsIds.products || !!assetsIds.environments,
);

export const selectCurrentUploadingFileStatus = createSelector(
  selectCurrentUploadingFile,
  (file) => file?.status,
);

export const selectCountFiles = createSelector(
  uploaderFeature.selectFiles,
  (files) => files?.length || 0,
);

export const selectIsAllAssetFinishedProcess = createSelector(
  uploaderFeature.selectFiles,
  selectCountFiles,
  (files, countFiles) =>
    countFiles > 0 && files?.every((file) => file.status === 'complete'),
);

export const selectUploaderTitle = createSelector(
  selectIsAllAssetFinishedProcess,
  selectCurrentUploadingFileStatus,
  selectCurrentUploadingFileIndex,
  selectCountFiles,
  selectProcessingFiles,
  (
    isAllAssetFinishedProcess,
    currentFileStatus,
    currentFileIndex,
    countFiles,
    processingFiles,
  ) => {
    if (isAllAssetFinishedProcess) {
      return 'Upload Completed';
    }
    if (currentFileStatus === 'uploading' && currentFileIndex !== null) {
      return `${currentFileIndex + 1} of ${countFiles} ${currentFileStatus}`;
    }
    if (currentFileStatus === 'error') {
      return `Upload problem`;
    }
    if (processingFiles?.length > 0) {
      return `Processing ${processingFiles?.length} file(s)`;
    }
    return 'Upload';
  },
);

export const selectIfNeedUpdateProductList = createSelector(
  selectPreparedReadyAssetIds,
  selectIsAllAssetFinishedProcess,
  (assetsIds, isAllAssetFinished) => isAllAssetFinished && !!assetsIds.products,
);

export const selectIfNeedUpdateEnvironmentList = createSelector(
  selectPreparedReadyAssetIds,
  selectIsAllAssetFinishedProcess,
  (assetsIds, isAllAssetFinished) =>
    isAllAssetFinished && !!assetsIds.environments,
);

export const selectIfNeedUpdateMaterialList = createSelector(
  selectPreparedReadyAssetIds,
  selectIsAllAssetFinishedProcess,
  (assetsIds, isAllAssetFinished) =>
    isAllAssetFinished && !!assetsIds.materials,
);
